import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import SectionWrapper from "@/components/base/SectionWrapper";
import { supabase, type Post } from "@/lib/supabase";

export default function PostDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<Post | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const { data: postData } = await supabase
        .from("posts")
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();

      if (!postData) {
        navigate("/events");
        return;
      }

      setPost(postData);

      const { data: relatedData } = await supabase
        .from("posts")
        .select("*")
        .eq("status", "published")
        .neq("id", postData.id)
        .order("created_at", { ascending: false })
        .limit(3);
      setRelatedPosts(relatedData || []);
      setIsLoading(false);
    };
    fetchData();
  }, [slug, navigate]);

  const getCategoryColor = (cat: string) => {
    switch (cat) { case "event": return "bg-gold-500"; case "news": return "bg-primary-600"; default: return "bg-emerald-600"; }
  };
  const getCategoryLabel = (cat: string) => {
    switch (cat) { case "event": return "Event"; case "news": return "News"; default: return "Announcement"; }
  };

  if (isLoading) {
    return <div className="min-h-[60vh] flex items-center justify-center bg-gray-50"><div className="w-12 h-12 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!post) return null;

  return (
    <div>
      <div className="relative h-[300px] md:h-[450px] overflow-hidden">
        <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <Link to="/events" className="inline-flex items-center gap-1.5 text-white/80 text-sm hover:text-white transition-colors mb-3">
              <i className="ri-arrow-left-line" /> Back to News &amp; Events
            </Link>
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium text-white mb-3 ${getCategoryColor(post.category)}`}>
              {getCategoryLabel(post.category)}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">{post.title}</h1>
          </div>
        </div>
      </div>

      <SectionWrapper className="py-10 md:py-16 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center gap-3 md:gap-5 text-sm mb-8 pb-8 border-b border-gray-100">
            {post.event_date && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-50 border border-gold-200">
                <i className="ri-calendar-event-line text-gold-500" />
                <span className="text-gold-700 font-semibold">
                  {new Date(post.event_date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" })}
                </span>
              </div>
            )}
            <div className="flex items-center gap-3 text-gray-400">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line text-xs" />
                Published {new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </span>
              <span className="w-1 h-1 rounded-full bg-gray-300" />
              <span>ACS Student Chapter</span>
            </div>
          </div>
          <p className="text-gray-600 text-lg leading-relaxed mb-8 italic border-l-4 border-gold-400 pl-4">{post.excerpt}</p>
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{post.content}</p>
          </div>
        </div>
      </SectionWrapper>

      {relatedPosts.length > 0 && (
        <SectionWrapper className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h3 className="font-heading font-bold text-xl md:text-2xl text-primary-900 mb-6">Related Posts</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedPosts.map((rp) => (
                <Link key={rp.id} to={`/events/${rp.slug}`} className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all">
                  <div className="h-36 md:h-40 overflow-hidden">
                    <img src={rp.featured_image} alt={rp.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  </div>
                  <div className="p-4">
                    <h4 className="font-heading font-semibold text-sm text-primary-900 group-hover:text-gold-700 transition-colors line-clamp-2">{rp.title}</h4>
                    <p className="text-gray-400 text-xs mt-1.5">{new Date(rp.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </SectionWrapper>
      )}
    </div>
  );
}